import React from "react";
import { HiLockClosed } from "react-icons/hi2";
import styles from "./AchievementBadge.module.css";
import {
  getAchievementBadgeIconKey,
  getAchievementBadgeIconPath,
} from "./AchievementBadgeIcons";
import type {
  AchievementBadgeAnimationType,
  AchievementBadgeViewModel,
} from "../types/view-models";

type AchievementBadgeProps = {
  badge: AchievementBadgeViewModel;
  unlocked: boolean;
  tier: number;
  animationType: AchievementBadgeAnimationType;
};

function getDisplayLabel(badge: AchievementBadgeViewModel): string {
  return String(badge.label || badge.name || "Achievement").trim() || "Achievement";
}

function getTierTone(tier: number): "low" | "mid" | "high" {
  if (tier >= 5) return "high";
  if (tier >= 3) return "mid";
  return "low";
}

function getAnimationClass(animationType: AchievementBadgeAnimationType, unlocked: boolean): string {
  if (!unlocked) return "";

  switch (animationType) {
    case "SHIMMER":
      return styles.animationShimmer || "";
    case "PULSE":
      return styles.animationPulse || "";
    case "ROTATE":
      return styles.animationRotate || "";
    case "NONE":
    default:
      return "";
  }
}

export default function AchievementBadge({
  badge,
  unlocked,
  tier,
  animationType,
}: AchievementBadgeProps) {
  const displayLabel = getDisplayLabel(badge);
  const tierTone = getTierTone(tier);
  const effectiveAnimation = unlocked ? animationType : "NONE";
  const animationClass = getAnimationClass(effectiveAnimation, unlocked);
  const accessibleState = unlocked ? "unlocked" : "locked";
  const iconKey = getAchievementBadgeIconKey(String(badge.key || ""));
  const iconPath = getAchievementBadgeIconPath(String(badge.key || ""));

  return (
    <article
      className={[
        styles.badge || "",
        styles[`tier${tierTone.charAt(0).toUpperCase()}${tierTone.slice(1)}`] || "",
        unlocked ? styles.unlocked || "" : styles.locked || "",
        animationClass,
      ]
        .filter(Boolean)
        .join(" ")}
      aria-label={`${displayLabel} badge, ${accessibleState}`}
      data-animation={effectiveAnimation.toLowerCase()}
      data-locked={unlocked ? "false" : "true"}
      data-tier-tone={tierTone}
    >
      <div className={styles.mediaWrap || ""}>
        <div className={styles.halo || ""} aria-hidden="true" />
        <div className={styles.iconFrame || ""} data-icon-key={iconKey}>
          <img
            src={iconPath}
            alt=""
            className={styles.iconSvg || ""}
            loading="lazy"
            decoding="async"
          />
        </div>
        {!unlocked && (
          <div className={styles.lockOverlay || ""} aria-label="Locked badge">
            <span className={styles.lockIcon || ""} aria-hidden="true">
              <HiLockClosed />
            </span>
          </div>
        )}
      </div>

      <div className={styles.content || ""}>
        <div className={styles.labelRow || ""}>
          <span className={styles.label || ""}>{displayLabel}</span>
          <span className={styles.tierChip || ""}>Tier {Math.max(1, Math.floor(tier || 1))}</span>
        </div>
        {badge.category ? <span className={styles.category || ""}>{badge.category}</span> : null}
      </div>
    </article>
  );
}
